/** Strings of the flow diagnosis on `/agents`: `flowDiagnosisEn` is the source, `flowDiagnosisCs` must have the same keys. */
export const flowDiagnosisEn = {
  flowDiagnosis: {
    title: "Diagnosis",
    intro:
      "How tickets flowed over the last seven days compared with the week before: who held them, how fast they closed, what came back and why agent runs failed. Made every Monday at 07:00.",
    loadFailed: "Couldn't load the diagnosis.",
    runFailed: "The diagnosis didn't finish.",
    pick: "Snapshot",
    runNow: "Run now",
    running: "Running…",
    none: "No diagnosis yet. Run it now or wait for Monday morning.",
    same: "same as last week",
    up: "▲ {{value}} vs last week",
    down: "▼ {{value}} vs last week",
    na: "n/a",
    hours: "{{value}} h",
    window: "Window",
    until: "to",
    weekly: "weekly",
    manual: "run by hand",
    kpi: {
      closed: "Closed tickets",
      cycle: "Development to close, median / p90",
      autonomous: "Closed without a person",
      returns: "Returns to development",
      failedRuns: "Failed agent runs",
      plans: "Plan approval, median / p90",
    },
    returnsValue: "{{count}} on {{tickets}} tickets, max {{max}}",
    holdersTitle: "Time of open tickets (hours)",
    holder: "Held",
    thisWeek: "This week",
    lastWeek: "Last week",
    holders: {
      human: "With people",
      human_backlog: "Backlog with people",
      agent_working: "Agent, in a state it works in",
      agent_idle: "Agent, in a state it doesn't work in",
      parked: "Parked (waiting on a dependency)",
      unassigned: "Unassigned",
    },
    failuresTitle: "Why runs failed",
    noFailures: "No failed runs.",
    kinds: {
      ticket_unreadable: "Ticket unreadable",
      transition_refused: "Status move refused",
      push_refused: "Push refused",
      checks_failed: "Checks failed",
      environment: "Broken environment",
      model_error: "Model error",
      subscription_hold: "Subscription on hold",
      timeout: "Timeout",
      interrupted: "Interrupted by a restart",
      crashed: "Crashed",
      nothing_to_do: "Nothing to change",
      needs_person: "Needs a person",
      steps_rejected: "Plan steps rejected",
      unknown: "Unknown",
    },
    costTitle: "Cost per agent",
    agentRuns: "{{runs}} runs, {{failed}} failed",
    costPerClosed: "Per closed ticket, median / p90",
    bucketsTitle: "Where tickets stood longest",
    reasonsTitle: "Top escalation and return reasons",
    failedTicketsTitle: "Tickets with the most failed runs",
    returnsTitle: "Tickets returned most often",
    suggestions: "Agent suggestions: {{created}} made, {{approved}} accepted.",
    idleUndecided: "not known yet",
    idleRung:
      "While idle, rung {{rung}}: {{created}} proposals, {{approved}} accepted; accepted share {{share}}.",
    empty: "Nothing this week.",
    people: {
      title: "Waiting on people",
      person: "Person",
      open: "Open",
      blocking: "Blocking",
      overdue: "Overdue",
      decided: "Decided",
      median: "Median to decide",
      ratio: "{{decided}} of {{asked}}",
    },
    autonomy: {
      returns:
        "{{slug}}: autonomy lowered {{from}} → {{to}}, {{returned}} of {{closed}} closed tickets came back after merge.",
      incident:
        "{{slug}}: autonomy lowered {{from}} → {{to}} after a production error that followed a merge.",
    },
  },
};

export const flowDiagnosisCs: typeof flowDiagnosisEn = {
  flowDiagnosis: {
    title: "Diagnostika",
    intro:
      "Jak tikety tekly za posledních sedm dní proti týdnu předtím: kdo je držel, jak rychle se zavíraly, co se vracelo a proč běhy agentů padaly. Počítá se každé pondělí v 7:00.",
    loadFailed: "Diagnostiku se nepodařilo načíst.",
    runFailed: "Diagnostika nedoběhla.",
    pick: "Snímek",
    runNow: "Spustit teď",
    running: "Počítám…",
    none: "Zatím žádná diagnostika. Spusťte ji teď, nebo počkejte na pondělní ráno.",
    same: "stejně jako minule",
    up: "▲ {{value}} proti minulému týdnu",
    down: "▼ {{value}} proti minulému týdnu",
    na: "není",
    hours: "{{value}} h",
    window: "Období",
    until: "až",
    weekly: "týdenní",
    manual: "spuštěno ručně",
    kpi: {
      closed: "Uzavřené tikety",
      cycle: "Od vývoje do zavření, medián / p90",
      autonomous: "Uzavřeno bez člověka",
      returns: "Návraty do vývoje",
      failedRuns: "Spadlé běhy agentů",
      plans: "Schválení plánu, medián / p90",
    },
    returnsValue: "{{count}} na {{tickets}} tiketech, max {{max}}",
    holdersTitle: "Čas otevřených tiketů (hodiny)",
    holder: "U koho",
    thisWeek: "Tento týden",
    lastWeek: "Minulý týden",
    holders: {
      human: "U lidí",
      human_backlog: "Backlog u lidí",
      agent_working: "U agenta, ve stavu kde pracuje",
      agent_idle: "U agenta, ve stavu kde nepracuje",
      parked: "Zaparkováno (čeká na závislost)",
      unassigned: "Bez řešitele",
    },
    failuresTitle: "Proč běhy padaly",
    noFailures: "Žádný spadlý běh.",
    kinds: {
      ticket_unreadable: "Tiket nejde přečíst",
      transition_refused: "Posun stavu odmítnut",
      push_refused: "Push odmítnut",
      checks_failed: "Kontroly neprošly",
      environment: "Rozbité prostředí",
      model_error: "Chyba modelu",
      subscription_hold: "Předplatné pozastaveno",
      timeout: "Vypršel čas",
      interrupted: "Přerušeno restartem",
      crashed: "Pád",
      nothing_to_do: "Není co měnit",
      needs_person: "Potřebuje člověka",
      steps_rejected: "Kroky plánu neprošly",
      unknown: "Neznámý důvod",
    },
    costTitle: "Náklady podle agenta",
    agentRuns: "{{runs}} běhů, {{failed}} spadlo",
    costPerClosed: "Na uzavřený tiket, medián / p90",
    bucketsTitle: "Kde tikety stály nejdéle",
    reasonsTitle: "Nejčastější důvody eskalací a vrácení",
    failedTicketsTitle: "Tikety s nejvíc spadlými běhy",
    returnsTitle: "Nejčastěji vracené tikety",
    suggestions: "Návrhy agentů: {{created}} vytvořeno, {{approved}} přijato.",
    idleUndecided: "zatím neznámý",
    idleRung:
      "Z volné chvíle, příčka {{rung}}: {{created}} návrhů, {{approved}} přijato; podíl přijatých {{share}}.",
    empty: "Tento týden nic.",
    people: {
      title: "Čeká na lidi",
      person: "Člověk",
      open: "Otevřeno",
      blocking: "Blokuje",
      overdue: "Po termínu",
      decided: "Rozhodnuto",
      median: "Medián do rozhodnutí",
      ratio: "{{decided}} z {{asked}}",
    },
    autonomy: {
      returns:
        "{{slug}}: autonomie snížena {{from}} → {{to}}, {{returned}} z {{closed}} uzavřených tiketů se vrátilo po mergi.",
      incident:
        "{{slug}}: autonomie snížena {{from}} → {{to}} po chybě v produkci, která následovala merge.",
    },
  },
};
